import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, CircularProgress, Collapse, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import { CustomPhoneController } from '../common/CustomPhoneController';
import { PhoneType, UpdateContactInput, useUpdateContactMutation } from '../../generated';
import palette from '../../../../shared/lib/theme/palette';
import { ContactDetailHeading, flexCenter } from '../../../../shared/lib/theme/styleConstants';
import { useAppContext } from '../../app/contexts/AppContext';
import { EditContactsProps } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { editContactValidationSchema } from '../../app/validationSchema';
import { CustomModal } from '../common/CustomModal';
import { formatPhoneNumber } from '../../../../shared/lib/utils';
import {
  BUTTON_TEXT_CANCEL,
  BUTTON_TEXT_SAVE,
  LABEL_EDIT_CONTACT,
  LABEL_EMAIL,
  LABEL_NAME_OF_PERSON,
  LABEL_NICK_NAME,
  LABEL_NOT_AVAILABLE,
  LABEL_PHONE_NUMBER,
  LABEL_PHONE_TYPE,
  NOTE_TRUSTED_CONTACT_SMS_NOTICE,
  PHONE_TYPE_CELL,
  PHONE_TYPE_LANDLINE
} from '../../../../shared/lib/constants';

export const EditContact: FC<EditContactsProps> = ({ currentCardData, refetch }) => {
  const { borderLight } = palette;
  const { name, nickName, phoneNumber, phoneType, id, email } = currentCardData ?? {};
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { isEditContact, dispatch, currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { subscriptionCancelled } = isSubscribed ?? {};
  const phoneTypeWithoutUnderscore = phoneType === PhoneType.LandLine ? 'Landline' : phoneType;
  const [showSmsWarning, setShowSmsWarning] = useState<boolean>(phoneType === PhoneType.Cell);

  const resetFields = () => {
    reset({
      name: name ?? '',
      nickName: nickName ?? '',
      phoneNumber: phoneNumber ?? '',
      phoneType: phoneType,
      email: email ?? ''
    });
  };

  const handleClose = () => {
    dispatch({ type: ActionType.SET_IS_EDIT_CONTACT, isEditContact: false });
    setIsEditOpen(false);
    resetFields();
  };

  const methods = useForm<UpdateContactInput, any, UpdateContactInput>({
    mode: 'all',

    // @ts-expect-error
    resolver: yupResolver(editContactValidationSchema),

    defaultValues: {
      name: name ?? '',
      nickName: nickName ?? '',
      phoneNumber: phoneNumber ?? '',
      phoneType: phoneType ?? PhoneType.Cell,
      email: email ?? ''
    }
  });

  const { handleSubmit, control, setValue, reset, watch, trigger } = methods;

  useEffect(() => {
    name && setValue('name', name);
    nickName && setValue('nickName', nickName);
    phoneType && setValue('phoneType', phoneType);
    phoneNumber && setValue('phoneNumber', phoneNumber);
    email && setValue('email', email);
  }, [name, nickName, phoneType, email, phoneNumber]);

  const [updateContact, { loading }] = useUpdateContactMutation({
    onError({ message }) {
      Alert.error(message);
      setIsEditOpen(false);
      resetFields();
    },

    onCompleted(data) {
      const { updateContact } = data || {};

      if (updateContact) {
        dispatch({ type: ActionType.SET_CURRENT_CARD, currentCardData: updateContact });
        handleClose();
        refetch();
        setIsEditOpen(false);
      }
    }
  });

  const onSubmit: SubmitHandler<UpdateContactInput> = async (data) => {
    const { name, nickName, phoneNumber, phoneType, email } = data ?? {};
    if (id) {
      await updateContact({
        variables: {
          updateContactInput: {
            id,
            name,
            nickName,
            phoneNumber: phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}`,
            phoneType,
            email: email ?? undefined
          }
        }
      });
    }
  };

  return (
    <CustomModal maxWidth="500px" isOpen={isEditContact} handleClose={handleClose}>
      <Box padding="0 8px">
        <Box position="relative">
          <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', padding: '24px 0' }}>
            {LABEL_EDIT_CONTACT}
          </Typography>

          <Box position="absolute" right={0} top={13}>
            <IconButton
              color="primary"
              size="large"
              onClick={() => {
                setIsEditOpen(!isEditOpen);
                resetFields();
              }}
            >
              {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
            </IconButton>
          </Box>
        </Box>

        <FormProvider {...methods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Collapse in={!isEditOpen}>
              <Grid container spacing={3} rowSpacing={0.5}>
                <Grid item xs={12}>
                  <Typography variant="h2" sx={ContactDetailHeading}>{LABEL_NICK_NAME}</Typography>
                  <Typography sx={{ marginTop: '10px', marginBottom: '20px' }} variant="subtitle2">
                    {nickName ?? LABEL_NOT_AVAILABLE}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h2" sx={ContactDetailHeading}>{LABEL_NAME_OF_PERSON}</Typography>
                  <Typography sx={{ marginTop: '10px', marginBottom: '20px' }} variant="subtitle2">
                    {name ?? LABEL_NOT_AVAILABLE}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h2" sx={ContactDetailHeading}>{LABEL_EMAIL}</Typography>
                  <Typography sx={{ marginTop: '10px', marginBottom: '20px' }} variant="subtitle2">
                    {email || LABEL_NOT_AVAILABLE}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={12} xs={12}>
                  <Typography variant="h2" sx={ContactDetailHeading}>{LABEL_PHONE_TYPE}</Typography>
                  <Typography sx={{ marginTop: '10px', marginBottom: '20px' }} variant="subtitle2">
                    {phoneTypeWithoutUnderscore ?? LABEL_NOT_AVAILABLE}
                  </Typography>
                </Grid>

                <Grid item md={8} sm={12} xs={12}>
                  <Typography variant="h2" sx={ContactDetailHeading}>{LABEL_PHONE_NUMBER}</Typography>
                  <Typography sx={{ marginTop: '10px', marginBottom: '20px' }} variant="subtitle2">
                    {formatPhoneNumber(phoneNumber ?? '') || LABEL_NOT_AVAILABLE}
                  </Typography>
                </Grid>
              </Grid>
            </Collapse>

            <Collapse in={isEditOpen}>
              <Grid container spacing={3} rowSpacing={0.5}>
                <Grid item xs={12}>
                  <CustomController controllerLabel={LABEL_NICK_NAME} controllerName="nickName" />
                </Grid>

                <Grid item xs={12}>
                  <CustomController controllerLabel={LABEL_NAME_OF_PERSON} controllerName="name" />
                </Grid>

                <Grid item xs={12}>
                  <CustomController controllerLabel={LABEL_EMAIL} controllerName="email" />
                </Grid>

                <Grid item md={4} xs={12}>
                  <FormControl sx={{ marginTop: '8px' }} fullWidth>
                    <InputLabel id="demo-simple-select-label">{LABEL_PHONE_TYPE}</InputLabel>

                    <Controller
                      name="phoneType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          defaultValue={phoneType}
                          label={LABEL_PHONE_TYPE}
                          onChange={async (event) => {
                            const {
                              target: { value }
                            } = event;
                            field.onChange(value);
                            if (value === PhoneType.Cell) {
                              setValue('email', watch('email'));
                              await trigger(['email']);
                            } else {
                              setValue('email', watch('email'));
                              await trigger(['email']);
                            }
                            setShowSmsWarning(value === PhoneType.Cell);
                          }}
                        >
                          <MenuItem value={PhoneType.Cell}>{PHONE_TYPE_CELL}</MenuItem>
                          <MenuItem value={PhoneType.LandLine}>{PHONE_TYPE_LANDLINE}</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item md={8} xs={12}>
                  <CustomPhoneController controllerLabel={LABEL_PHONE_NUMBER} controllerName="phoneNumber" />
                </Grid>
              </Grid>

              {showSmsWarning && (
                <Box>
                  <Typography sx={flexCenter} variant="h2" color="primary" mt="8px">
                    <ErrorIcon sx={{ mr: '5px' }} /> {NOTE_TRUSTED_CONTACT_SMS_NOTICE}
                  </Typography>
                </Box>
              )}
            </Collapse>

            <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
              <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                {BUTTON_TEXT_CANCEL}
              </Button>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || (subscriptionCancelled ?? false)}
                endIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {BUTTON_TEXT_SAVE}
              </Button>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </CustomModal>
  );
};
